import { jsxs, jsx } from 'react/jsx-runtime';
import React__default, { useState, useLayoutEffect } from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useMergeRefs, FloatingPortal } from '@floating-ui/react';
import { space, shadow, size } from '../../constants.js';
import { useTooltip, TooltipContext, useTooltipContext } from './TooltipContext.js';
import { palette } from '../../palette/index.js';

const BORDER_RADIUS = {
    small: 6,
    medium: 8,
    large: 10
};
function Tooltip({ children, content, ...options }) {
    if (options.disabled || !content) {
        return children;
    }
    return jsxs(Tooltip.Root, {
        ...options,
        children: [
            jsx(Tooltip.Trigger, {
                ...options,
                children: children
            }),
            jsx(Tooltip.Content, {
                style: options.cssForTooltipContainer,
                children: content
            })
        ]
    });
}
const TooltipRoot = ({ children, content: _, ...options })=>{
    const tooltip = useTooltip(options);
    return jsx(TooltipContext.Provider, {
        value: tooltip,
        children: children
    });
};
Tooltip.Root = TooltipRoot;
const TooltipContent = /*#__PURE__*/ React__default.forwardRef(function TooltipContent({ style, ...props }, propRef) {
    const context = useTooltipContext();
    const [height, setHeight] = useState(0);
    const [element, setElement] = useState(null);
    const callbackRef = React__default.useCallback((node)=>{
        setElement(node);
    }, []);
    const ref = useMergeRefs([
        context.refs.setFloating,
        propRef,
        callbackRef
    ]);
    useLayoutEffect(()=>{
        if (!element) return;
        const observer = new ResizeObserver((entries)=>{
            const entry = entries[0];
            if (entry) {
                setHeight(entry.contentRect.height);
            }
        });
        observer.observe(element);
        return ()=>observer.disconnect();
    }, [
        element,
        context.open
    ]);
    const getBorderRadius = (h)=>{
        if (h >= 200) return BORDER_RADIUS.large;
        if (h >= 100) return BORDER_RADIUS.medium;
        return BORDER_RADIUS.small;
    };
    if (!context.open) return null;
    const borderRadius = getBorderRadius(height);
    return jsx(FloatingPortal, {
        children: jsx(TooltipWrapper, {
            children: jsx(TooltipContentContainer, {
                ref: ref,
                style: {
                    ...context.floatingStyles,
                    ...style,
                    ...shadow("tooltip"),
                    borderRadius
                },
                ...context.getFloatingProps(props),
                "data-color-scheme": "dark"
            })
        })
    });
});
Tooltip.Content = TooltipContent;
const TooltipTrigger = /*#__PURE__*/ React__default.forwardRef(function TooltipTrigger({ asChild = false, children, cssForTooltipWrapper }, propRef) {
    const context = useTooltipContext();
    const childrenRef = children.ref;
    const ref = useMergeRefs([
        context.refs.setReference,
        propRef,
        childrenRef
    ]);
    if (asChild && /*#__PURE__*/ React__default.isValidElement(children)) {
        return /*#__PURE__*/ React__default.cloneElement(children, {
            ref,
            ...children.props,
            "data-state": context.open ? "open" : "closed"
        });
    }
    return jsx("span", {
        ref: ref,
        "data-state": context.open ? "open" : "closed",
        style: cssForTooltipWrapper,
        children: children
    });
});
Tooltip.Trigger = TooltipTrigger;
const softEnter = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;
const TooltipWrapper = styled.div({
    display: "block",
    position: "relative"
});
const TooltipContentContainer = styled.div({
    wordBreak: "break-word",
    backgroundColor: palette.gray1,
    color: palette.gray14,
    padding: space.s2,
    fontSize: size.s2,
    ...shadow("tooltip"),
    position: "relative",
    overflow: "hidden",
    maxWidth: space.s8,
    zIndex: 9999,
    animation: `${softEnter} 120ms ease-out`,
    a: {
        color: palette.green12
    }
});

export { Tooltip };
